/**
 * What a tap on the Card or Wallet option does on the screen before a start
 * (Warren, 2026-10-25: "when I hit loyalty wallet it takes me to the wallet
 * top-up page. It should do that if the wallet is empty (like mine is). But
 * Driver should be able to toggle between selections before hitting start.").
 *
 *   - A usable option is selected, and nothing navigates: the driver can
 *     toggle Card and Wallet freely before Start.
 *   - A wallet that cannot pay yet is not selected (Start would not pay
 *     with it) and does not navigate on the tap: it opens its reason with a
 *     "Top up" action, which goes to the wallet page in the currency and
 *     amount the start needs. The card stays the selection.
 *   - A card that cannot pay keeps its behaviour: locked says until when;
 *     no card on file goes to add one.
 *
 * Pure, so node tests drive it directly; PaymentSelector applies the result.
 */
import type { WalletOptionView } from './walletOptionView';

export type PaymentSource = 'card' | 'wallet';

export type PaymentTap =
  | { kind: 'select'; source: PaymentSource }
  | { kind: 'explainWallet' }
  | { kind: 'cardLocked' }
  | { kind: 'addCard' };

export interface PaymentTapContext {
  cardEnabled: boolean;
  cardLockedOut: boolean;
  walletSelectable: boolean;
}

export function paymentTap(option: PaymentSource, ctx: PaymentTapContext): PaymentTap {
  if (option === 'wallet') {
    return ctx.walletSelectable ? { kind: 'select', source: 'wallet' } : { kind: 'explainWallet' };
  }
  if (ctx.cardEnabled) return { kind: 'select', source: 'card' };
  return ctx.cardLockedOut ? { kind: 'cardLocked' } : { kind: 'addCard' };
}

/** The radio each option shows: checked only when it is the selection AND
 *  it can pay, so the checked option is always what Start pays with. */
export function paymentChecked(selected: PaymentSource | null, ctx: PaymentTapContext): Record<PaymentSource, boolean> {
  return {
    card: selected === 'card' && ctx.cardEnabled,
    wallet: selected === 'wallet' && ctx.walletSelectable,
  };
}

/** The label of the "Top up" action under a wallet that cannot pay yet:
 *  "Top up CA$10.00", or "Top up wallet" without an amount. Null when no
 *  top-up can help (fleet, guest, outstanding balance, no rate). */
export function walletTopUpCopy(
  view: Pick<WalletOptionView, 'topUpPath' | 'shortfallCents' | 'topUpCurrency'>,
  money: (cents: number, currency: string) => string,
): { key: string; values: Record<string, string> } | null {
  if (!view.topUpPath) return null;
  if (view.shortfallCents > 0) {
    return { key: 'selector.walletTopUp', values: { amount: money(view.shortfallCents, view.topUpCurrency) } };
  }
  return { key: 'gate.topUpWallet', values: {} };
}
